import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";

/**
 * Лендинг gordost.gim94.ru
 *
 * Финальный одобренный вариант: Swiss-минимализм
 * (см. https://gordost.gim94.ru/_designs/03-final-v1.html).
 *
 * Сетка — 12 колонок (CSS Grid), типографика Manrope 200pt для крупного,
 * 600 для меток. Палитра: чёрный/белый + cobalt #0033ff акцент.
 *
 * Статистика секции 02 подтягивается с публичного API
 * `GET /api/gordost/public/stats` (без auth). При сетевой ошибке —
 * fallback на хардкод-значения, чтобы лендинг не выглядел сломанным.
 */

interface PublicStats {
  students: number | null;
  classes: number | null;
  teachers: number | null;
  movement_events_current_year: number | null;
  year_label: string;
  first_reception_label: string;
}

// Fallback показывается только при сетевой ошибке /public/stats. Значения
// выровнены с реальными цифрами активного года (классов 40, не 79), чтобы
// при недоступности API лендинг не показывал заведомо неверное число.
const STATS_FALLBACK: PublicStats = {
  students: 1074,
  classes: 40,
  teachers: 77,
  movement_events_current_year: 41,
  year_label: "2025/2026",
  first_reception_label: "Май 2027",
};

function formatNum(n: number | null): string {
  if (n == null) return "—";
  // Тонкий пробел как разделитель тысяч (Swiss-style)
  return n.toLocaleString("ru-RU").replace(/ /g, " ");
}

export function LandingPage() {
  const { data } = useQuery<PublicStats>({
    queryKey: ["gordost", "public", "stats"],
    queryFn: async () => {
      const res = await api.get<PublicStats>("/public/stats");
      return res.data;
    },
    // Если запрос упал — используем fallback, не показываем ошибку юзеру.
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 минут
  });

  const stats = data ?? STATS_FALLBACK;

  return (
    <div className="bg-white text-[#0a0a0a] font-[var(--font-display)]">
      {/* TOPBAR (sticky) */}
      <div className="topbar border-b border-black py-[18px] sticky top-0 bg-white z-50">
        <div className="swiss-grid">
          <div className="col-span-3 text-[12px] font-bold tracking-[0.05em] uppercase flex items-center">
            <img
              src="/logo-gim94.png"
              alt="Гимназия №94"
              className="w-7 h-7 mr-2 object-contain align-middle"
            />
            Гордость 94
          </div>
          <div className="col-span-5 text-[12px] text-[var(--color-muted)] uppercase tracking-[0.05em] hidden md:block">
            МАОУ Гимназия №&nbsp;94 · Екатеринбург
          </div>
          <div className="md:col-span-4 col-span-9 text-right text-[12px] font-medium">
            <Link
              to="/login"
              className="text-black no-underline ml-6 border-b border-transparent hover:border-black pb-[2px] transition-[border-color]"
            >
              Войти
            </Link>
            <Link
              to="/register"
              className="text-black no-underline ml-6 border-b border-transparent hover:border-black pb-[2px] transition-[border-color]"
            >
              Регистрация по коду
            </Link>
          </div>
        </div>
      </div>

      {/* HERO */}
      <section className="hero pt-24 pb-16 border-b border-black">
        <div className="swiss-grid items-end">
          <div className="col-span-3 text-[11px] font-bold tracking-[0.16em] uppercase text-[var(--color-cobalt)] self-start">
            <span className="block w-9 h-[3px] bg-[var(--color-cobalt)] mb-3.5" />
            Первый приём
            <br />
            {stats.first_reception_label}
          </div>
          <h1
            className="col-span-9 m-0 font-display font-[200] leading-[0.88] tracking-[-0.04em]"
            style={{ fontSize: "clamp(72px, 12vw, 220px)" }}
          >
            Гордость&nbsp;94
          </h1>

          <div className="col-span-9 md:col-span-6 mt-12 text-[21px] font-normal leading-[1.4] max-w-[680px]">
            Родительский кабинет, в котором семья весь год собирает{" "}
            <span className="text-[var(--color-cobalt)]">
              летопись достижений
            </span>{" "}
            ребёнка. В мае директор лично приглашает лучших на торжественный
            приём в актовом зале.
          </div>

          <div className="col-span-9 md:col-span-3 mt-12 flex flex-col gap-3 self-end">
            <Link to="/login" className="btn-swiss btn-swiss--primary">
              <span>Войти в кабинет</span>
              <span className="font-light text-[18px]">→</span>
            </Link>
            <Link to="/register" className="btn-swiss btn-swiss--ghost">
              <span>Регистрация по коду</span>
              <span className="font-light text-[18px]">→</span>
            </Link>
          </div>

          <div className="col-span-3 mt-12 self-end">
            <div
              className="font-[200] leading-none text-[var(--color-cobalt)] tracking-[-0.04em]"
              style={{ fontSize: "80px" }}
            >
              2026
            </div>
            <div className="text-[11px] tracking-[0.16em] uppercase text-[var(--color-muted)] mt-1">
              Учебный год · Том&nbsp;I
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 01 — О чём речь */}
      <section className="py-[120px] border-b border-black">
        <div className="swiss-grid">
          <div className="col-span-1 text-[13px] font-semibold tracking-[0.16em] uppercase text-[var(--color-cobalt)]">
            01 —
          </div>
          <div className="col-span-2 text-[13px] font-semibold tracking-[0.16em] uppercase">
            О чём речь
          </div>
          <h2
            className="col-span-9 font-[200] leading-none tracking-[-0.025em] mb-12"
            style={{ fontSize: "clamp(48px, 7vw, 96px)" }}
          >
            Не за оценки.
            <br />
            За <span className="text-[var(--color-cobalt)]">поступок</span>.
          </h2>

          <div
            className="col-span-7 text-[36px] font-[300] leading-[1.2] tracking-[-0.01em] border-t border-black pt-6 mt-9"
          >
            «Я хочу знать каждого ребёнка, который{" "}
            <span className="text-[var(--color-cobalt)] font-normal">
              вложил себя
            </span>{" "}
            во что-то — в науку, в спорт, в добрые дела, в творчество. Не
            отличников — а тех, кто старается».
            <div className="text-[11px] tracking-[0.16em] uppercase text-[var(--color-muted)] mt-6 font-semibold">
              — Директор гимназии №&nbsp;94
            </div>
          </div>

          <ul className="col-span-4 mt-9 list-none p-0 m-0">
            {[
              ["01", <>Дневник, журнал и оценки <strong>не учитываются</strong>.</>],
              ["02", <>Родитель публикует достижения, классный руководитель проверяет.</>],
              ["03", <>Дипломы, олимпиады, проекты, добрые дела, увлечения вне школы.</>],
              ["04", <>Один кабинет на семью — на всех детей сразу.</>],
              ["05", <>Личное приглашение от директора — не пресс-конференция.</>],
            ].map(([n, text], i) => (
              <li
                key={i}
                className={
                  "grid grid-cols-[32px_1fr] gap-3 py-[18px] border-b border-[var(--color-line)] text-[15px] leading-[1.45]" +
                  (i === 0 ? " border-t border-black" : "")
                }
              >
                <span className="font-bold text-[var(--color-cobalt)] tabular-nums">
                  {n}
                </span>
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SECTION 02 — Гимназия в цифрах */}
      <section className="py-[120px] border-b border-black">
        <div className="swiss-grid">
          <div className="col-span-1 text-[13px] font-semibold tracking-[0.16em] uppercase text-[var(--color-cobalt)]">
            02 —
          </div>
          <div className="col-span-2 text-[13px] font-semibold tracking-[0.16em] uppercase">
            Гимназия в цифрах
          </div>
          <h2
            className="col-span-9 font-[200] leading-none tracking-[-0.025em] mb-12"
            style={{ fontSize: "clamp(48px, 7vw, 96px)" }}
          >
            Школа большая.
            <br />А приём — <span className="text-[var(--color-cobalt)]">личный</span>.
          </h2>

          <div
            className="col-span-12 grid grid-cols-1 md:grid-cols-5 mb-8"
            style={{ gap: "1px", background: "#0a0a0a", border: "1px solid #0a0a0a" }}
          >
            <StatCard
              num={formatNum(stats.students)}
              cap="Учеников"
              desc="От 1 до 11 класса. Все могут стать гостем Приёма."
            />
            <StatCard
              num={formatNum(stats.classes)}
              cap="Классов"
              desc="Начальная, средняя, старшая — все три ступени."
            />
            <StatCard
              num={formatNum(stats.teachers)}
              cap="Учителей"
              desc="Каждый классный руководитель — глазами и руками этого приёма."
            />
            <StatCard
              num={formatNum(stats.movement_events_current_year)}
              cap={
                <>
                  Мероприятий&nbsp;
                  <span className="text-[var(--color-cobalt)]">{stats.year_label}</span>
                </>
              }
              desc="В 6 молодёжных движениях: Движение Первых, Орлята, Юнармия и других."
            />
            <StatCard
              num={stats.first_reception_label}
              cap="Первый приём"
              desc="Через 12 месяцев директор лично пригласит первых лучших — тех, кого вы покажете в кабинете в этот учебный год."
              accent
            />
          </div>

          <div className="col-span-12 text-[14px] leading-[1.6] text-[var(--color-muted)] max-w-[680px] mt-6 italic">
            Это первый запуск формата. Прошлогодних призёров пока нет — летопись
            начинается с сентября&nbsp;2026 и закрывается торжественным приёмом
            весной.
          </div>
        </div>
      </section>

      {/* SECTION 03 — Как начать */}
      <section className="py-[120px] border-b border-black">
        <div className="swiss-grid">
          <div className="col-span-1 text-[13px] font-semibold tracking-[0.16em] uppercase text-[var(--color-cobalt)]">
            03 —
          </div>
          <div className="col-span-2 text-[13px] font-semibold tracking-[0.16em] uppercase">
            Как начать
          </div>
          <h2
            className="col-span-9 font-[200] leading-none tracking-[-0.025em] mb-12"
            style={{ fontSize: "clamp(48px, 7vw, 96px)" }}
          >
            Три шага.
            <br />
            Десять <span className="text-[var(--color-cobalt)]">минут</span>.
          </h2>

          <div className="col-span-12 grid md:grid-cols-3 gap-6">
            {[
              {
                n: "01",
                t: "Получите код",
                body: "На родительском собрании классный руководитель раздаёт распечатанные QR-коды — по одному на ребёнка. Если пропустили собрание — попросите код у классрука лично.",
                meta: "1 минута",
              },
              {
                n: "02",
                t: "Заведите кабинет",
                body: "Откройте gordost.gim94.ru, отсканируйте QR или введите код. Email + пароль — и кабинет готов. Несколько детей в гимназии? Добавьте каждого отдельным кодом.",
                meta: "5 минут",
              },
              {
                n: "03",
                t: "Публикуйте достижения",
                body: "Фото грамоты, описание проекта, рассказ о добром деле. Прикрепите файлы или ссылку на видео. Классрук одобряет — пост виден директору.",
                meta: "в течение года",
              },
            ].map((s) => (
              <div
                key={s.n}
                className="border-t-[3px] border-[var(--color-cobalt)] pt-6 flex flex-col min-h-[280px]"
              >
                <div
                  className="font-[200] leading-[0.9] text-[var(--color-cobalt)] mb-6 tracking-[-0.04em]"
                  style={{ fontSize: "80px" }}
                >
                  {s.n}
                </div>
                <h3 className="text-[24px] font-medium leading-[1.1] mb-3.5">
                  {s.t}
                </h3>
                <p className="text-[14px] leading-[1.6] text-[#333] mb-auto">
                  {s.body}
                </p>
                <div className="text-[11px] tracking-[0.16em] uppercase text-[var(--color-muted)] mt-6">
                  {s.meta}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 04 — FAQ */}
      <section className="py-[120px] border-b border-black">
        <div className="swiss-grid">
          <div className="col-span-1 text-[13px] font-semibold tracking-[0.16em] uppercase text-[var(--color-cobalt)]">
            04 —
          </div>
          <div className="col-span-2 text-[13px] font-semibold tracking-[0.16em] uppercase">
            Часто спрашивают
          </div>
          <h2
            className="col-span-9 font-[200] leading-none tracking-[-0.025em] mb-12"
            style={{ fontSize: "clamp(48px, 7vw, 96px)" }}
          >
            Что важно <span className="text-[var(--color-cobalt)]">знать</span>.
          </h2>

          <div
            className="col-span-12 grid md:grid-cols-2"
            style={{ gap: "24px 60px" }}
          >
            {[
              {
                q: "Это конкурс? Будут места?",
                a: "Нет. Это не соревнование между детьми и не альтернатива олимпиадам. Это разговор директора лично с теми учениками, чьим путём гимназия гордится.",
              },
              {
                q: "А если у ребёнка нет грамот?",
                a: "Грамоты — не критерий. Важно, что ребёнок старался, проявлял себя, помогал, искал, делал. Опишите своими словами — и приложите фото или видео, если есть.",
              },
              {
                q: "Кто видит мои публикации?",
                a: "Классный руководитель (модерирует), завуч (если нужна страховка), директор (для приглашения), вы и ваш ребёнок. Никаких социальных лент, лайков и других родителей.",
              },
              {
                q: "Школа использует мои данные?",
                a: "Только для ведения портфолио вашего ребёнка. Хранение — на российских серверах, по ФЗ-152. Кнопка «Удалить аккаунт и все данные» доступна в кабинете в любой момент.",
              },
              {
                q: "Сколько хранятся публикации?",
                a: "Все 11 школьных лет — это накопительное портфолио. После выпуска можно скачать как PDF и забрать с собой. Через 3 года после выпуска школа закрывает аккаунт.",
              },
              {
                q: "Кто решает, кого пригласить?",
                a: "Директор. Читает портфолио каждого ребёнка лично. Завуч помогает с рекомендациями, классруки — с модерацией. Решение — за директором.",
              },
            ].map((f, i) => (
              <div key={i} className="border-t border-black py-5">
                <div className="text-[17px] font-semibold mb-2 tracking-[-0.01em]">
                  {f.q}
                </div>
                <div className="text-[14px] leading-[1.6] text-[#333]">
                  {f.a}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INNER CTA (тёмный блок) */}
      <section className="py-[120px] bg-black text-white">
        <div className="swiss-grid">
          <h2
            className="col-span-12 md:col-span-8 font-[200] leading-none tracking-[-0.025em] text-white"
            style={{ fontSize: "clamp(48px, 7vw, 108px)" }}
          >
            Гимназия <span className="text-[var(--color-red)]">№&nbsp;94</span>.
            <br />
            Год начинается&nbsp;сейчас.
          </h2>
          <div className="col-span-12 md:col-span-8 mt-8 text-[16px] leading-[1.5] text-[#bbb] max-w-[540px]">
            До первого Приёма — несколько месяцев. У вас впереди целый учебный
            год, чтобы рассказать директору о своём ребёнке.
          </div>
          <div className="col-span-12 md:col-span-4 mt-8 md:mt-0 flex flex-col gap-3 justify-end">
            <Link
              to="/login"
              className="btn-swiss btn-swiss--icta-primary"
            >
              <span>Войти в кабинет</span>
              <span className="font-light text-[18px]">→</span>
            </Link>
            <Link
              to="/register"
              className="btn-swiss btn-swiss--icta-ghost"
            >
              <span>Регистрация по коду</span>
              <span className="font-light text-[18px]">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-9 border-t border-black">
        <div className="swiss-grid">
          <div className="col-span-4 text-[13px] font-bold tracking-[0.05em] uppercase flex items-center">
            <img
              src="/logo-gim94.png"
              alt="Гимназия №94"
              className="w-6 h-6 mr-2 object-contain align-middle"
            />
            Гордость 94
          </div>
          <div className="col-span-5 text-[11px] tracking-[0.12em] uppercase text-[var(--color-muted)] leading-[1.6]">
            МАОУ Гимназия №&nbsp;94
            <br />
            620075, Екатеринбург, ул. Бажова, 139
          </div>
          <div className="col-span-3 text-[11px] text-right text-[var(--color-muted)] leading-[1.6]">
            gordost.gim94.ru · 2026
            <br />
            <a
              href="https://gim94.ru"
              className="text-[var(--color-muted)] no-underline border-b border-[var(--color-line)] hover:text-black hover:border-black"
            >
              gim94.ru
            </a>
          </div>
        </div>
      </footer>

      {/* Локальные стили для swiss-сетки и кнопок (Tailwind 4 не покрывает
          12-col grid с произвольными gap'ами одной утилитой). */}
      <style>{`
        .swiss-grid {
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          gap: 24px;
          max-width: 1440px;
          margin: 0 auto;
          padding: 0 48px;
        }
        .hero .swiss-grid,
        .topbar .swiss-grid { /* sticky topbar — без вертикальных паддингов */ }

        .btn-swiss {
          display: inline-flex;
          align-items: center;
          justify-content: space-between;
          padding: 18px 24px;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          text-decoration: none;
          transition: all 0.15s ease;
        }
        .btn-swiss--primary {
          background: #0033ff;
          color: #ffffff;
        }
        .btn-swiss--primary:hover { background: #0a0a0a; color: #ffffff; }
        .btn-swiss--ghost {
          background: transparent;
          color: #0a0a0a;
          border: 1px solid #0a0a0a;
        }
        .btn-swiss--ghost:hover { background: #0a0a0a; color: #ffffff; }
        .btn-swiss--icta-primary {
          background: #0033ff;
          color: #ffffff;
        }
        .btn-swiss--icta-primary:hover { background: #ff3a1d; color: #ffffff; }
        .btn-swiss--icta-ghost {
          background: transparent;
          color: #ffffff;
          border: 1px solid #ffffff;
        }
        .btn-swiss--icta-ghost:hover { background: #ffffff; color: #0a0a0a; }

        @media (max-width: 900px) {
          .swiss-grid {
            padding: 0 20px;
            grid-template-columns: repeat(4, 1fr);
          }
          .swiss-grid > * {
            grid-column: 1 / -1 !important;
          }
        }
      `}</style>
    </div>
  );
}

interface StatCardProps {
  num: string;
  cap: React.ReactNode;
  desc: string;
  accent?: boolean;
}

function StatCard({ num, cap, desc, accent = false }: StatCardProps) {
  const bg = accent ? "#0033ff" : "#ffffff";
  const fg = accent ? "#ffffff" : "#0a0a0a";
  const capColor = accent ? "rgba(255,255,255,0.75)" : "#717171";
  const descColor = accent ? "rgba(255,255,255,0.92)" : "#3a3a3a";

  return (
    <div
      className="flex flex-col p-9 md:px-6 min-h-[300px]"
      style={{ background: bg, color: fg }}
    >
      <div
        className="font-[200] leading-[0.95] tracking-[-0.04em] mb-6"
        style={{
          fontSize: accent
            ? "clamp(36px, 4vw, 56px)"
            : "clamp(48px, 5.5vw, 84px)",
        }}
      >
        {num}
      </div>
      <div
        className="text-[11px] font-bold tracking-[0.16em] uppercase mb-4"
        style={{ color: capColor }}
      >
        {cap}
      </div>
      <div
        className="text-[13px] leading-[1.55] mt-auto"
        style={{ color: descColor }}
      >
        {desc}
      </div>
    </div>
  );
}
